import 'public-path';
import { SiteConfig } from '@99designs/frontend-components/apps/global-nav/lib/Site';
import { loadableReady } from '@loadable/component';

import 'isomorphic-fetch';
import React, { useEffect } from 'react';
import { ApolloProvider } from '@apollo/react-components';
import { hydrate } from 'react-dom';
import { hot } from 'react-hot-loader';
import { addLocaleData } from 'react-intl';
import { ThemeProvider } from '@material-ui/core/styles';
import { BrowserRouter } from 'react-router-dom';
// @ts-ignore
import localeData from 'ReactIntlLocaleData'; // eslint-disable-line import/no-unresolved

import createBrowserClient from 'common/ApolloClient/createBrowserClient';

import { AppConfigData, AppConfigProvider } from 'common/AppConfigContext';

import { ExperimentsData, ExperimentsProvider } from 'common/ExperimentsContext';
import { AccountsJs } from './AccountsJs';
import App from './App';
import materialTheme from './materialTheme';
import { HelmetProvider } from 'react-helmet-async';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

// Setup here
const backend = createBrowserClient({
    endpoint: '/app/graphql',
    cacheDiv: 'apollo-init-data-backend',
});

const siteDataNode = document.getElementById('site-init-data');
const siteState = JSON.parse(siteDataNode!.textContent!); // eslint-disable-line @typescript-eslint/no-non-null-assertion

const experimentsDataNode = document.getElementById('experiments-init-data');
const experimentsData: ExperimentsData = experimentsDataNode?.textContent
    ? JSON.parse(experimentsDataNode.textContent)
    : { experiments: '' };

const appConfigNode = document.getElementById('app-config-init-data');
const appConfigData: AppConfigData = appConfigNode?.textContent
    ? JSON.parse(appConfigNode.textContent)
    : {};

const HotReloadableRoot = hot(module)(
    ({ language, siteConfig }: { language: string; siteConfig: SiteConfig }) => {
        useEffect(() => {
            const jssStyles = document.querySelector('#jss-server-side');
            if (jssStyles?.parentElement) {
                jssStyles.parentElement.removeChild(jssStyles);
            }
        }, []);
        return (
            <ThemeProvider theme={materialTheme}>
                <App
                    language={language}
                    siteConfig={siteConfig}
                    backendSubscriptionClient={backend.subscriptionClient}
                />
                <AccountsJs />
            </ThemeProvider>
        );
    }
);

const renderApp = () => {
    const htmlNode = document.getElementsByTagName('html');
    const language = htmlNode[0].getAttribute('lang')!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

    // Initialise react-intl locale
    addLocaleData(localeData);

    Bugsnag.start({
        apiKey: appConfigData.bugsnagApiKey || '_',
        appType: __BUGSNAG_APP_TYPE__,
        appVersion: appConfigData.appVersion || 'dev',
        enabledReleaseStages: ['production'],
        releaseStage: appConfigData.bugsnagReleaseStage,
        enabledBreadcrumbTypes: [
            'navigation',
            'request',
            'process',
            'log',
            'user',
            'state',
            'error',
            'manual',
        ],
        plugins: [new BugsnagPluginReact(React)],
    });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const BugsnagErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary();

    hydrate(
        <BugsnagErrorBoundary>
            <ApolloProvider client={backend.combinedClient}>
                <AppConfigProvider value={appConfigData}>
                    <ExperimentsProvider value={experimentsData}>
                        <BrowserRouter>
                            <HelmetProvider>
                                <HotReloadableRoot language={language} siteConfig={siteState} />
                            </HelmetProvider>
                        </BrowserRouter>
                    </ExperimentsProvider>
                </AppConfigProvider>
            </ApolloProvider>
        </BugsnagErrorBoundary>,
        document.getElementById('root'),
        () => {
            window.initialised = true;
        }
    );
};

if (!window.Intl) {
    document.write(
        '<script src="https://assets.99static.com/shared/js/Intl.min-856e66ee99.js"></script>'
    );
}

window.init = () => {
    loadableReady(() => {
        renderApp();
    });
};

if (module.hot) {
    module.hot.accept();
}
